/**
 * Чистая половина разбора отмены Ozon: УСПЕЛИ ЛИ МЫ СДАТЬ ПОСЫЛКУ.
 *
 * Вопрос владельца 09.09.26 к строке короба: «cancel же может быть после того,
 * как мы уже её сдали». Он решает, где вещь физически. Отмена ДО сдачи
 * оставляет её в коробе — идти и доставать руками. Отмена ПОСЛЕ означает, что
 * вещь уехала к Ozon, сколько бы CRM ни считала её нашей.
 *
 * Ответов на этот вопрос ДВА, и они из разных систем:
 *
 *     кабинет   `cancelled_after_ship` — что говорит сам Ozon
 *     наш учёт  акт перевозки — то, по чему списывает шедулер
 *
 * Обычно они сходятся. Замер 09.09.26 по всем 25 отменённым отправлениям
 * зеркала: 5 отмен «до» (акта нет, вещь у нас), 17 отмен «после» (все в акте,
 * автомат их разбирает) — и ТРИ, где ответы РАСХОДЯТСЯ: Ozon говорит «после
 * отгрузки», а строки в акте у отправления нет вовсе. Ровно эти три и висят в
 * коробах Ozon, ровно про них и был вопрос.
 *
 * Расхождение экран НЕ РЕШАЕТ за человека и не прячет: показывает оба ответа и
 * помечает строку. Признак списания при этом не трогается — им остаётся акт.
 *
 * Здесь нет ни React, ни DOM: слова разбора обязан проверять тест, а не глаз
 * на живом экране.
 */

import type { FbsCancel } from "@/lib/providers";

export type CancelVerdict = {
  /** Короткая подпись: успели сдать или нет. */
  label: string;
  /** Пояснение под ней: чем подтверждено и что было до отмены. */
  note: string;
  /**
   * Два ответа разошлись: кабинет говорит «отгружено», а акта нет.
   *
   * Это не косметика — такую строку человек обязан разобрать руками: либо
   * вещь уехала и её надо списать, либо она в коробе и кабинет ошибся.
   */
  disputed: boolean;
};

/** Хвост пояснения: кто отменил и почему. Пусто — кабинет не сказал. */
function why(cancel: FbsCancel): string {
  return [cancel.by, cancel.reason].filter(Boolean).join(": ");
}

/** «было: передаётся в доставку» — состояние отправления ДО отмены. */
function was(cancel: FbsCancel): string {
  return cancel.before_label ? `было: ${cancel.before_label}` : "";
}

function join(parts: string[]): string {
  return parts.filter(Boolean).join(" · ");
}

/**
 * Разбор отмены в слова. `null` — отправление не отменено, разбирать нечего.
 *
 * `inAct` — НАШ ответ: попало ли отправление в акт перевозки. У строки короба
 * это `shipped` (тот же отбор, по которому спишет шедулер), у карточки задачи —
 * непустой `carriage_id`. Оба говорят об одном, и потому довод здесь один.
 */
export function cancelVerdict(
  cancel: FbsCancel | null | undefined,
  inAct: boolean,
): CancelVerdict | null {
  if (!cancel) return null;

  // Кабинет промолчал — это третий ответ, а не «до отгрузки». Молчание чужой
  // системы нельзя превращать в утверждение: вещь при таком ответе ищут
  // глазами, а не по подписи на экране.
  if (cancel.after_ship === null) {
    return {
      label: "отменено",
      note: join(["до или после отгрузки — Ozon не сказал", was(cancel), why(cancel)]),
      disputed: false,
    };
  }

  if (!cancel.after_ship) {
    return {
      label: "отменено ДО отгрузки",
      note: join(["сдать не успели — вещь в коробе", was(cancel), why(cancel)]),
      disputed: false,
    };
  }

  if (inAct) {
    return {
      label: "отменено ПОСЛЕ отгрузки",
      note: join(["посылка уехала актом перевозки", was(cancel), why(cancel)]),
      disputed: false,
    };
  }

  return {
    label: "отменено ПОСЛЕ отгрузки",
    note: join([
      "но в акт перевозки отправление не попало",
      was(cancel),
      why(cancel),
    ]),
    disputed: true,
  };
}
